import {useEffect, useRef, useState} from 'react'

import {intersection, remove} from 'es-toolkit'

import useLocalStorageState from './use-local-storage-state'

export const useExpandedRows = <IdType>(storageKey: string, getIds: () => Array<IdType>) => {
  const [storedExpandedMap, setStoredExpandedMap] = useLocalStorageState<Array<IdType>>(
    storageKey,
    []
  )
  const [expandedRowIds, setExpandedRowIds] = useState<Array<IdType>>(storedExpandedMap || [])

  const expandedRowIdsRef = useRef(expandedRowIds)
  expandedRowIdsRef.current = expandedRowIds

  const getIdsRef = useRef(getIds)
  getIdsRef.current = getIds

  useEffect(() => {
    return () => {
      const allIds = getIdsRef.current()
      // Filter ids for whether they actually exist in the getIds array.
      const filteredSelectedIds = intersection(expandedRowIdsRef.current, allIds)
      setStoredExpandedMap(filteredSelectedIds)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isExpanded = (id: IdType) => expandedRowIds.includes(id)

  const removeExpanded = (id: IdType) => setExpandedRowIds(remove(expandedRowIds, n => n !== id))

  const addExpanded = (id: IdType) => setExpandedRowIds([...expandedRowIds, id])

  const toggleExpansion = (id: IdType) => {
    if (isExpanded(id)) {
      removeExpanded(id)
    } else {
      addExpanded(id)
    }
  }

  const setAllExpanded = (value: boolean) => {
    if (value) {
      setExpandedRowIds(getIds())
    } else {
      setExpandedRowIds([])
    }
  }

  const isAnyExpanded = () => expandedRowIds.length > 0

  return {
    expandedRowIds,
    isExpanded,
    toggleExpansion,
    setAllExpanded,
    isAnyExpanded,
  }
}
